import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { RequestCard, STAGE_META } from "@/components/admin/requests/RequestCard";

const SOURCES = [["", "All invoices"], ["public", "Site submissions"], ["deals", "Spreadsheet uploads"]];

/** Every invoice — customer submissions and spreadsheet uploads — in one pipeline with stage + source filters. */
export const RequestsPipeline = ({ reloadKey }) => {
  const [data, setData] = useState({ rows: [], stage_counts: {} });
  const [stage, setStage] = useState("action_needed");
  const [source, setSource] = useState("");
  const load = () => adminApi.requests({ ...(stage ? { stage } : {}), ...(source ? { source } : {}) }).then(setData).catch(() => toast.error("Failed to load invoices"));
  useEffect(() => { load(); }, [stage, source, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const patch = async (id, p) => { try { await adminApi.patchRequest(id, p); load(); } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } };
  const [sending, setSending] = useState(null);
  const sendEmail = async (id) => {
    setSending(id);
    try { await adminApi.sendInvoiceEmail(id); toast.success("Invoice email sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Email failed"); load(); }
    setSending(null);
  };
  const total = Object.values(data.stage_counts || {}).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-3" data-testid="o4b-requests">
      <div className="flex gap-1 border-b border-[#E4E4E0]" data-testid="invoice-source-filter">
        {SOURCES.map(([id, label]) => (
          <button key={id} onClick={() => setSource(id)} data-testid={`invoice-source-${id || "all"}`}
            className={`px-3 h-8 border-b-2 font-head text-[11px] font-bold uppercase tracking-wider ${source === id ? "border-[#E3000B] text-[#E3000B]" : "border-transparent text-gray-500 hover:text-black"}`}>{label}</button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap items-center" data-testid="o4b-stage-filter">
        {Object.entries(STAGE_META).map(([id, m]) => (
          <button key={id} onClick={() => setStage(id)} data-testid={`o4b-req-filter-${id}`} title={m.hint}
            className={`h-8 px-3 rounded-sm border font-mono text-[11px] uppercase inline-flex items-center gap-2 ${stage === id ? "text-white border-transparent" : "border-[#E4E4E0] text-gray-600 hover:text-black"}`}
            style={stage === id ? { background: m.color } : {}}>
            <i className="w-2 h-2 rounded-full" style={{ background: stage === id ? "#fff" : m.color }} />{m.label}
            <b data-testid={`o4b-req-count-${id}`}>{data.stage_counts?.[id] || 0}</b>
          </button>
        ))}
        <button onClick={() => setStage("")} data-testid="o4b-req-filter-all"
          className={`h-8 px-3 rounded-sm border font-mono text-[11px] uppercase ${stage === "" ? "bg-black text-white border-black" : "border-[#E4E4E0] text-gray-600 hover:text-black"}`}>All {total}</button>
      </div>
      {data.rows.length === 0 && <div className="p-8 text-center text-gray-500 font-mono text-xs border border-[#E4E4E0] rounded-sm" data-testid="o4b-req-empty">{stage ? `Nothing in "${STAGE_META[stage].label}" right now.` : "No invoices yet."}</div>}
      {data.rows.map(r => <RequestCard key={r.request_id} r={r} onPatch={patch} onSendEmail={sendEmail} sending={sending === r.request_id} onDeleted={load} />)}
    </div>
  );
};
